import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import ProviderBuilder from 'assets/providers/ProviderBuilder';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { date, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { scrollToElement } from 'assets/utils/dom/Scroll';
import { isEmail, isEmailDomain } from 'assets/utils/parsersAndValidation/Validators';
import { HttpStatus } from 'config/Api.Config';
import { claimsPermissions } from 'config/Auth.Config';
import { first, isEmpty, omit, orderBy, set } from 'lodash';
import Claim from 'models/claims/claim/Model.Claim';
import claimSaveApi from 'models/claims/claim/save/Api.Claim.Save';
import claimSetStatusApi from 'models/claims/claim/setStatus/Api.Claim.SetStatus';
import ClaimComment from 'models/claims/claimComment/ClaimComment';
import claimCommentDeleteApi from 'models/claims/claimComment/delete/Api.ClaimComment.Delete';
import claimCommentLikeApi from 'models/claims/claimComment/like/Api.ClaimComment.Like';
import claimCommentSaveApi from 'models/claims/claimComment/save/Api.ClaimComment.Save';
import claimEmailSendEmailApi from 'models/claims/claimEmail/sendEmail/Api.ClaimEmail.SendEmail';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import claimJobListApi from 'models/claims/claimJob/list/Api.ClaimJob.List';
import claimJobSaveApi from 'models/claims/claimJob/save/Api.ClaimJob.Save';
import ClaimJobStatusAuditLog from 'models/claims/claimJobStatusAuditLog/Model.ClaimJobStatusAuditLog';
import ClaimOwnershipLog from 'models/claims/claimOwnershipLog/Model.ClaimOwnershipLog';
import ClaimStatusAuditLog from 'models/claims/claimStatusAuditLog/Model.ClaimStatusAuditLog';
import ClaimType from 'models/claims/claimType/Model.ClaimType';
import EmailTemplate from 'models/core/claimEmailTemplate/Model.ClaimEmailTemplate';
import ClaimCommentType from 'models/enums/ClaimCommentType';
import ClaimStatusCode from 'models/enums/ClaimStatusCode';
import PaymentStatus from 'models/enums/PaymentStatus';
import Product from 'models/productManagement/product/Model.Product';
import { useEffect, useMemo, useState } from 'react';
import { claimEditIndexRoute } from './ClaimEdit.Index';

export interface ClaimParams {
  claimId: string;
}
export interface ClaimQuery {
  list?: Api.Core.Claim.List.IRequest;
  newClaimId?: number;
  kickedBy?: string;
}
interface StatusAuditLog extends Utils.IStatusAuditLog {
  claimId?: number;
  claim?: Claim;
  claimJobId?: number;
  claimJob?: ClaimJob;
}
export type ClaimTab =
  | 'info'
  | 'alerts'
  | 'jobs'
  | 'documents'
  | 'invoices'
  | 'ownershipLogs'
  | 'comments'
  | 'logs'
  | 'emails';
export type ClaimEditActions = 'editClientInfo' | 'editContactInfo' | 'updateClaim';
export type EmailAction = 'sendEmail' | 'changeStatusSendEmail';

export function useClaimEditPageRouter() {
  return usePageRouter<ClaimParams, ClaimQuery>({ route: claimEditIndexRoute });
}
export function useClaimEditorCore() {
  const pageRouter = useClaimEditPageRouter();
  const { lang } = getLocales();
  const { userId, currentPermissions: currentUserPermissions, permissions } = useAuthStore();
  const [lastModifiedJob, setLastModifiedJob] = useState<Model.IClaimJob>(undefined);
  const [selectedClaim, selectClaim] = useState<Model.IClaim>(undefined);
  const [emailAction, setEmailAction] = useState<EmailAction>();
  const [claimEditAction, setClaimEditAction] = useState<ClaimEditActions>();

  //#REGION PERMISSIONS
  const isEditable = permissions?.EDIT_CLAIM;
  const isClaimAdmin = permissions?.CLAIM_ADMIN;
  const isDeletable = permissions?.DELETE_CLAIM;
  const canEditClientInfo = permissions?.EDIT_CLAIM_CUSTOMERINFO;
  const canEditContactInfo = permissions?.EDIT_CLAIMPAYEE;
  const canViewClaimAlertNotifications = isClaimAdmin || permissions?.VIEW_CLAIM_ALERT_NOTIFICATIONS;
  const hasAnyPermission = claimsPermissions.some((it) => currentUserPermissions.includes(it));

  const hasAnyClaimInfoPermission =
    isClaimAdmin ||
    canEditClientInfo ||
    canEditContactInfo ||
    permissions?.VIEW_CLAIM_CONTRACTINFO ||
    permissions?.VIEW_CLAIM_CUSTOMERINFO ||
    permissions?.VIEW_CLAIMPAYEE ||
    permissions?.VIEW_CLAIM_VEHICLEINFO;

  const hasAnyClaimDocumentPermission =
    isClaimAdmin ||
    permissions?.VIEW_CLAIMDOCUMENT ||
    permissions?.EDIT_CLAIMDOCUMENT ||
    permissions?.UPLOAD_CLAIMDOCUMENT ||
    permissions?.DOWNLOAD_AllCLAIMDOCUMENT ||
    permissions?.DOWNLOAD_CLAIMDOCUMENT ||
    permissions?.DELETE_CLAIMDOCUMENT;

  const hasAnyClaimInvoicePermission =
    isClaimAdmin ||
    permissions?.VIEW_CLAIMINVOICE ||
    permissions?.EDIT_CLAIMINVOICE ||
    permissions?.UPLOAD_CLAIMINVOICE ||
    permissions?.DOWNLOAD_CLAIMINVOICE ||
    permissions?.DELETE_CLAIMINVOICE;

  const hasAnyClaimJobPermission =
    isClaimAdmin ||
    permissions?.EDIT_CLAIMJOB ||
    permissions?.CREATE_CLAIMJOB ||
    permissions?.DELETE_CLAIMJOB ||
    permissions?.UPDATE_CLAIMJOB_STATUS ||
    permissions?.VIEW_CLAIMJOBDOCUMENT ||
    permissions?.UPLOAD_CLAIMJOBDOCUMENT ||
    permissions?.DOWNLOAD_CLAIMJOBDOCUMENT ||
    permissions?.DELETE_CLAIMJOBDOCUMENT ||
    permissions?.VIEW_CLAIMJOBCOMMENT ||
    permissions?.CREATE_CLAIMJOBCOMMENT ||
    permissions?.EDIT_CLAIMJOBCOMMENT ||
    permissions?.DELETE_CLAIMJOBCOMMENT ||
    permissions?.VIEW_CLAIMJOBITEM ||
    permissions?.CREATE_CLAIMJOBITEM ||
    permissions?.EDIT_CLAIMJOBITEM ||
    permissions?.DELETE_CLAIMJOBITEM;

  const hasAnyExternalClaimCommentPermission =
    isClaimAdmin ||
    permissions?.VIEW_EXTERNAL_CLAIM_COMMENT ||
    permissions?.CREATE_EXTERNAL_CLAIM_COMMENT ||
    permissions?.EDIT_EXTERNAL_CLAIM_COMMENT ||
    permissions?.DELETE_EXTERNAL_CLAIM_COMMENT;

  const hasAnyInternalClaimCommentPermission =
    isClaimAdmin ||
    permissions?.VIEW_INTERNAL_CLAIM_COMMENT ||
    permissions?.CREATE_INTERNAL_CLAIM_COMMENT ||
    permissions?.EDIT_INTERNAL_CLAIM_COMMENT ||
    permissions?.DELETE_INTERNAL_CLAIM_COMMENT;

  const hasAnyClaimCommentPermission = hasAnyExternalClaimCommentPermission || hasAnyInternalClaimCommentPermission;

  const payeeRestriction = isClaimAdmin || permissions?.RESTRICT_EDIT_PAYEE_CLAIMJOBITEM;
  const canEditInsurerAdjudications = isClaimAdmin || permissions?.RESTRICT_EDIT_INSURER_CLAIMJOBITEM;
  const canEditAdjustedAmt = isClaimAdmin || permissions?.RESTRICT_EDIT_ADJUSTEDAMT_CLAIMJOBITEM;
  //#ENDREGION

  const claimId = parseInt(pageRouter.params.claimId);

  function navigateToTab(tab: ClaimTab) {
    setTimeout(
      () =>
        scrollToElement({
          querySelector: `#${tab}`,
          containerSelector: '.page',
          offsetTop: 200,
        }),
      0
    );
  }

  const findClaimApi = useApi(
    {
      action: Claim.find,
      body: { id: claimId },
      default: null,
      wait: !pageRouter.params.claimId,
      callback: (_, res) => {
        setLastModifiedJob(first(orderBy(res.payload?.claimJobs, ['modifiedAtUtc'], ['desc'])));
      },
    },
    [pageRouter.params.claimId]
  );
  const reloadClaim = () => findClaimApi.execute((b) => b);

  const findProductApi = useApi(
    {
      action: Product.find,
      wait: !findClaimApi.payload?.contract?.productId,
      body: { id: findClaimApi.payload?.contract?.productId },
    },
    [findClaimApi.payload?.contract?.productId]
  );

  const claimTypesApi = useApi({ action: ClaimType.list });
  const claimTypes = claimTypesApi.payload?.data;

  const listCommentsApi = useApi(
    {
      action: ClaimComment.list,
      body: !claimId && hasAnyClaimCommentPermission ? null : { claimId, claimCommentType: ClaimCommentType.external },
      wait: !claimId || !hasAnyExternalClaimCommentPermission,
      keepResponse: true,
    },
    [claimId, hasAnyExternalClaimCommentPermission]
  );

  const listClaimsApi = useApi(
    {
      action: Claim.list,
      body: !findClaimApi.payload?.contract?.id ? null : { contractId: [findClaimApi.payload?.contract?.id] },
      wait: !findClaimApi.payload?.contract?.id,
    },
    [findClaimApi.payload?.contract?.id]
  );

  const emailTemplatesApi = useApi({ action: EmailTemplate.list });
  const emailTemplates = emailTemplatesApi.payload?.data;

  const statusInfoApi = useApi({ action: Claim.getStatusFlowAndRequirements });
  const statusInfo = statusInfoApi.payload;

  async function sendEmail(emailTemplate: Model.IClaimEmailTemplate) {
    const res = await claimEmailSendEmailApi({
      params: { claimId: parseInt(pageRouter.params?.claimId) },
      body: emailTemplate,
    });

    if (res.status === HttpStatus.ok) {
      addLog({ success: lang.emailSuccessfullySent });
      setEmailAction(null);
      reloadClaim();
    } else {
      addLog({ error: res.message });
    }
  }

  function reload() {
    findClaimApi.execute((body) => body);
    statusInfoApi.execute();
    emailTemplatesApi.execute();
    if (hasAnyClaimCommentPermission) {
      listCommentsApi.execute((body) => body);
    }
  }

  const claim = findClaimApi.payload;
  const contract = claim?.contract;
  const product = findProductApi.payload ?? contract?.product;
  const internalComments = useMemo(
    () => listCommentsApi.payload?.data?.filter((it) => it.claimCommentType === ClaimCommentType.internal),
    [listCommentsApi.payload?.data]
  );
  const externalComments = useMemo(
    () => listCommentsApi.payload?.data?.filter((it) => it.claimCommentType === ClaimCommentType.external),
    [listCommentsApi.payload?.data]
  );

  const claimTypeWithReport = useMemo(
    () =>
      claimTypes?.find((claimType) =>
        claimType.products?.some((product) => product.id === (contract?.product?.id || contract?.productId))
      ),
    [claimTypes, contract]
  );

  const isClaimOwner = claim?.ownerId && claim.ownerId === userId;

  //#region FORM
  const form = useForm<Utils.FormData<Model.IClaim> & { emailTemplate?: Model.IClaimEmailTemplate }>(
    {
      default: claim || {},
      onSubmit: async (data) => {
        const finalClaim = {
          ...data,
          ownerId: claim?.ownerId,
          statusCode: claim?.statusCode || ClaimStatusCode.OPEN_ACTIVE_IN_PROGRESS,
        };
        const claimSaveResponse = await claimSaveApi(finalClaim);
        const claimId = finalClaim?.id || claimSaveResponse.payload;

        if (claimId?.toString() === pageRouter.params.claimId) {
          reload();
          selectClaim(undefined);
        } else {
          pageRouter.updateParams({
            claimId: claimId?.toString(),
          });
          navigateToTab('info');
        }
      },
      validation: (data, errors) => {
        if (data.emailTemplate) {
          if (
            !data.emailTemplate.additionalRecipientEmails?.some((it) => it) &&
            !data.emailTemplate.recipientsWithRole?.some((it) => it.id) &&
            !data.emailTemplate.sendToClaimOwner &&
            !data.emailTemplate.sendToClient &&
            !data.emailTemplate.sendToContactEmail
          )
            set(errors, 'emailTemplate.additionalRecipientEmails', lang.mustNotBeEmpty);
          if (
            data.emailTemplate.additionalRecipientEmails?.length &&
            data.emailTemplate.additionalRecipientEmails?.some((it) => !isEmail(it))
          )
            set(errors, 'emailTemplate.additionalRecipientEmails', lang.invalidEmailFormatRequires);
          if (!data.emailTemplate.subject) set(errors, 'emailTemplate.subject', lang.mustNotBeEmpty);
          if (!data.emailTemplate.name) set(errors, 'emailTemplate.name', lang.mustNotBeEmpty);
          if (!data.emailTemplate.senderEmail) set(errors, 'emailTemplate.senderEmail', lang.mustNotBeEmpty);
          if (!isEmailDomain(data.emailTemplate.senderEmail, 'goautoshield.com'))
            set(errors, 'emailTemplate.senderEmail', lang.mustBeEmailDomain.replace('{domain}', 'goautoshield.com'));
          if (!data.emailTemplate.senderName) set(errors, 'emailTemplate.senderName', lang.mustNotBeEmpty);
          if (!data.emailTemplate.htmlBody) set(errors, 'emailTemplate.htmlBody', lang.mustNotBeEmpty);
        }
      },
    },
    [claim]
  );

  const changeStatusForm = useForm<{
    nextStatus: Model.Enum.ClaimStatusCode;
    ownerId?: number;
    emailTemplate?: Model.IClaimEmailTemplate;
  }>(
    {
      default: {
        ownerId: claim?.ownerId,
        emailTemplate: null,
      },
      onSubmit: async ({ ownerId, nextStatus, emailTemplate }) => {
        const hasStatusChanged = nextStatus && nextStatus !== claim?.statusCode;

        if (hasStatusChanged && nextStatus === ClaimStatusCode.PAID_CLOSED_PAID) {
          const {
            payload: { data: claimJobs },
          } = await claimJobListApi({ claimId });

          if (!isEmpty(claimJobs)) {
            for (const claimJob of claimJobs) {
              const updatedItems = [];
              for (const claimJobItem of claimJob.claimJobItems ?? []) {
                if (claimJobItem.statusCode === PaymentStatus.unpaid) {
                  updatedItems.push({ ...claimJobItem, statusCode: PaymentStatus.paid });
                } else {
                  updatedItems.push({ ...claimJobItem });
                }
              }
              await claimJobSaveApi(omit({ ...claimJob, claimJobItems: updatedItems }, 'claimType'));
            }
          }
        }
        if (hasStatusChanged && claimId && ownerId) {
          const res = await claimSetStatusApi({ claimId, status: nextStatus, ownerId, emailTemplate });
          if (res.status === HttpStatus.ok) addLog({ success: lang.claimStatusSuccesfullyChanged });
          else addLog({ error: res.message });
        }
        if (claimId?.toString() === pageRouter.params.claimId) {
          reload();
          selectClaim(undefined);
        } else {
          pageRouter.updateParams({
            claimId: claimId?.toString(),
          });
          navigateToTab('info');
        }
      },
      validation: (data, errors) => {
        if (data.emailTemplate) {
          if (
            !data.emailTemplate.additionalRecipientEmails?.some((it) => it) &&
            !data.emailTemplate.recipientsWithRole?.some((it) => it.id) &&
            !data.emailTemplate.sendToClaimOwner &&
            !data.emailTemplate.sendToClient &&
            !data.emailTemplate.sendToContactEmail
          )
            set(errors, 'emailTemplate.additionalRecipientEmails', lang.mustNotBeEmpty);
          if (
            data.emailTemplate.additionalRecipientEmails?.length &&
            data.emailTemplate.additionalRecipientEmails?.some((it) => !isEmail(it))
          )
            set(errors, 'emailTemplate.additionalRecipientEmails', lang.invalidEmailFormatRequires);
          if (!data.emailTemplate.subject) set(errors, 'emailTemplate.subject', lang.mustNotBeEmpty);
          if (!data.emailTemplate.name) set(errors, 'emailTemplate.name', lang.mustNotBeEmpty);
          if (!data.emailTemplate.senderEmail) set(errors, 'emailTemplate.senderEmail', lang.mustNotBeEmpty);
          if (!isEmailDomain(data.emailTemplate.senderEmail, 'goautoshield.com'))
            set(errors, 'emailTemplate.senderEmail', lang.mustBeEmailDomain.replace('{domain}', 'goautoshield.com'));
          if (!data.emailTemplate.senderName) set(errors, 'emailTemplate.senderName', lang.mustNotBeEmpty);
          if (!data.emailTemplate.htmlBody) set(errors, 'emailTemplate.htmlBody', lang.mustNotBeEmpty);
        }
      },
    },
    [claim]
  );

  const nextStatus = changeStatusForm.data.nextStatus;
  const currentStatus = claim?.statusCode || ClaimStatusCode.OPEN_ACTIVE_IN_PROGRESS;
  const isStatusChanged = nextStatus && nextStatus !== currentStatus;
  //#endregion

  //#region ClaimOwnershipLogs
  const claimOwnershipLogListApi = useApi({
    action: ClaimOwnershipLog.list,
    wait: true,
  });

  useEffect(() => {
    if (claim?.id) {
      claimOwnershipLogListApi.execute({ claimId: [claim?.id] });
    } else {
      claimOwnershipLogListApi.reset(true);
    }
  }, [claim?.id]);
  //#endregion

  //#region TABS
  const tabs: Array<{ value: ClaimTab; label: string; isDisabled?: boolean; count?: number }> = [
    { value: 'info', isDisabled: !hasAnyClaimInfoPermission, label: lang.info },
    {
      value: 'alerts',
      isDisabled: !canViewClaimAlertNotifications,
      label: lang.alertsAndNotifications,
      count: claim?.claimAlerts?.length,
    },
    { value: 'jobs', isDisabled: !hasAnyClaimJobPermission, label: lang.jobs, count: claim?.claimJobs?.length },
    {
      value: 'comments',
      isDisabled: !hasAnyClaimCommentPermission,
      label: lang.comments,
      count: externalComments?.length,
    },
    {
      value: 'documents',
      isDisabled: !hasAnyClaimDocumentPermission,
      label: lang.documents,
      count: claim?.claimDocuments?.length,
    },
    {
      value: 'invoices',
      isDisabled: !hasAnyClaimInvoicePermission,
      label: lang.invoices,
      count: claim?.claimInvoices?.length,
    },
    {
      value: 'ownershipLogs',
      isDisabled: !(permissions?.VIEW_CLAIM_LOGS || isClaimAdmin),
      label: lang.ownershipLogs ?? 'Ownership Logs',
      count: claimOwnershipLogListApi?.payload?.data?.length,
    },
    {
      value: 'emails',
      isDisabled: !(permissions?.SEND_CLAIMEMAIL || permissions?.VIEW_CLAIMEMAIL_HISTORY || isClaimAdmin),
      label: lang.emails,
      count: claim?.claimEmails?.length,
    },
    { value: 'logs', isDisabled: !(permissions?.VIEW_CLAIM_LOGS || isClaimAdmin), label: lang.logs },
  ];

  function hasTabPermission(tab: string) {
    switch (tab) {
      case 'info':
        return hasAnyClaimInfoPermission;
      case 'alerts':
        return canViewClaimAlertNotifications;
      case 'jobs':
        return hasAnyClaimJobPermission;
      case 'comments':
        return hasAnyExternalClaimCommentPermission;
      case 'documents':
        return hasAnyClaimDocumentPermission;
      case 'invoices':
        return hasAnyClaimInvoicePermission;
      case 'ownershipLogs':
        return permissions?.VIEW_CLAIM_LOGS || permissions?.CLAIM_ADMIN;
      case 'emails':
        return permissions?.SEND_CLAIMEMAIL || permissions?.VIEW_CLAIMEMAIL_HISTORY || isClaimAdmin;
      case 'logs':
        return permissions?.VIEW_CLAIM_LOGS || permissions?.CLAIM_ADMIN;
      default:
        return false;
    }
  }
  const tabsWithPermissions = tabs.filter((tab) => hasTabPermission(tab.value));

  const isClaimCanceled = claim?.statusCode === ClaimStatusCode.CANCELED_VOID;
  const isClaimJobItemEditable =
    claim?.statusCode === ClaimStatusCode.OPEN_PENDING ||
    claim?.statusCode === ClaimStatusCode.PAID_CLOSED_PAID ||
    claim?.statusCode === ClaimStatusCode.DENIED_DECLINED ||
    isClaimCanceled;

  const isClaimInvoiceEditableByStatus = isClaimJobItemEditable;
  const isClaimJobEditableByStatus = isClaimInvoiceEditableByStatus;
  const isClaimDocumentEditableByStatus =
    isClaimCanceled ||
    claim?.statusCode === ClaimStatusCode.PAID_CLOSED_PAID ||
    claim?.statusCode === ClaimStatusCode.DENIED_DECLINED;

  //#region ClaimStatusAuditLogs
  const claimStatusAuditLogListApi = useApi({
    action: ClaimStatusAuditLog.list,
    wait: true,
  });
  useEffect(() => {
    if (claim?.id) {
      claimStatusAuditLogListApi.execute({ claimId: [claim?.id] });
    } else {
      claimStatusAuditLogListApi.reset(true);
    }
  }, [claim?.id]);
  const claimJobStatusAuditLogListApi = useApi({
    action: ClaimJobStatusAuditLog.list,
    wait: true,
  });
  useEffect(() => {
    if (claim?.id) {
      claimJobStatusAuditLogListApi.execute({ claimId: [claim?.id] });
    } else {
      claimJobStatusAuditLogListApi.reset(true);
    }
  }, [claim?.id]);
  const statusAuditLogs = useMemo(() => {
    const logs = [
      ...(claimStatusAuditLogListApi?.payload?.data ?? []),
      ...(claimJobStatusAuditLogListApi?.payload?.data ?? []),
    ];
    const claimMap = new Map<number, Model.IClaim>();
    (claimStatusAuditLogListApi?.payload?.data ?? []).forEach((log) => {
      if (log.claim?.id) claimMap.set(log.claim.id, log.claim);
    });

    return orderBy(
      logs?.map(
        (log: StatusAuditLog) =>
          ({
            ...log,
            claim: log.claim ?? log.claimJob?.claim ?? claimMap.get(log.claimJob?.claimId),
            claimId: log.claimId ?? log.claimJob?.claimId,
            claimJob: log.claimJob,
            claimJobId: log.claimJobId,
          }) as StatusAuditLog
      ),
      (it) => it.createdAtUtc
    );
  }, [claimStatusAuditLogListApi?.payload?.data, claimJobStatusAuditLogListApi?.payload?.data]);
  //#endregion

  const [hasRequestedClaim, setHasRequestedClaim] = useState(false);

  useEffect(() => {
    if (findClaimApi.isExecuting) {
      setHasRequestedClaim(true);
    }
  }, [findClaimApi.isExecuting]);

  return {
    form,
    hasReports: !!claimTypeWithReport?.claimTypeReportSettings?.length,
    isLoading:
      findClaimApi.isExecuting ||
      findProductApi.isExecuting ||
      listCommentsApi.isExecuting ||
      statusInfoApi.isExecuting ||
      emailTemplatesApi.isExecuting,
    isLoadingComments: listCommentsApi.isExecuting,
    pageRouter,
    hasAnyPermission,
    permissions,
    isDeletable,
    isEditable,
    isClaimAdmin,
    isClaimOwner,
    canViewClaimAlertNotifications,
    isClaimCanceled,
    claim,
    findClaimApi,
    product,
    contract,
    statusAuditLogs,
    internalComments,
    externalComments,
    emailTemplates,
    statusInfo,
    nextStatus,
    currentStatus,
    isStatusChanged,
    tabs: tabsWithPermissions,
    claimHistory: listClaimsApi.payload?.data,
    changeStatusForm,
    reload,
    reloadClaim,
    reloadComments: () => listCommentsApi.execute((b) => b),
    navigateToTab,
    selectClaim,
    selectedClaim,
    claimEditAction,
    setClaimEditAction,
    canEditClientInfo: isClaimJobEditableByStatus || !(isClaimAdmin || (canEditClientInfo && isClaimOwner)),
    canEditContactInfo: isClaimJobEditableByStatus || !(isClaimAdmin || (canEditContactInfo && isClaimOwner)),
    canEditClaim: isClaimJobEditableByStatus || !(isClaimAdmin || (isClaimOwner && permissions.EDIT_CLAIM)),
    emailAction,
    setEmailAction,
    sendEmail,
    currentUserPermissions,
    isClaimInvoiceEditableByStatus,
    isClaimJobEditableByStatus,
    isClaimJobItemEditable,
    isClaimDocumentEditableByStatus,
    hasAnyClaimCommentPermission,
    hasAnyClaimDocumentPermission,
    hasAnyClaimInfoPermission,
    hasAnyClaimJobPermission,
    hasAnyClaimInvoicePermission,
    lastModifiedJob,
    setLastModifiedJob,
    hasRequestedClaim,
    claimOwnershipLogListApi,
    payeeRestriction,
    canEditAdjustedAmt,
    canEditInsurerAdjudications,
  };
}

const { useProvider: useClaimEdit, Provider: ClaimEditProvider } = ProviderBuilder(useClaimEditorCore);

export { ClaimEditProvider, useClaimEdit };

type ClaimCommentsProps = {
  commentType: ClaimCommentType;
};
export function useClaimComments(props: ClaimCommentsProps) {
  const { lang } = getLocales();
  const {
    claim,
    reloadComments,
    externalComments,
    isLoadingComments,
    isClaimAdmin,
    permissions,
    isClaimOwner,
    isClaimJobEditableByStatus,
  } = useClaimEdit();
  const auth = useAuthStore();
  const [action, setAction] = useState<'edit' | 'delete'>();
  const form = useForm<Utils.FormData<Model.IClaimComment>>({
    default: {
      claimId: claim?.id,
      userId: auth.userId,
      claimCommentType: props.commentType,
      comment: undefined,
    },
    validation: (data, errors) => {
      if (!data.comment) errors.comment = lang.required;
    },
    onSubmit: async (data) => {
      const response = await claimCommentSaveApi({
        ...data,
        modifiedAtUtc: date().tz('UTC').format(defaultServerDateTimeFormat),
      });
      if (!response) addLog({ error: lang.unknownError });
      else {
        reloadComments();
      }
    },
  });
  useEffect(() => {
    if (claim?.id) {
      form.update({ claimId: claim?.id });
    }
  }, [claim?.id]);
  useEffect(() => {
    if (form.isSubmitted) {
      setAction(null);
      form.reload();
    }
  }, [form.isSubmitted]);

  const deleteApi = useApi({
    action: claimCommentDeleteApi,
    callback: () => {
      setAction(null);
      form.reload();
      reloadComments();
    },
    wait: true,
  });
  const likeApi = useApi({
    action: claimCommentLikeApi,
    callback: () => reloadComments(),
    wait: true,
  });

  return {
    form,
    deleteApi,
    likeApi,
    comments: props.commentType === ClaimCommentType.external && externalComments,
    isLoadingComments,
    action,
    setAction,
    isClaimAdmin,
    permissions,
    canViewComments: permissions?.VIEW_EXTERNAL_CLAIM_COMMENT || isClaimAdmin,
    canDeleteComments: isClaimJobEditableByStatus || !(permissions?.DELETE_EXTERNAL_CLAIM_COMMENT || isClaimAdmin),
    canAddComments: isClaimJobEditableByStatus || !(permissions?.CREATE_EXTERNAL_CLAIM_COMMENT || isClaimAdmin),
    canEditComments: isClaimJobEditableByStatus || !(permissions?.EDIT_EXTERNAL_CLAIM_COMMENT || isClaimAdmin),
    isClaimOwner,
  };
}
